import { CheckCircle2, Circle, Clock3, MinusCircle } from 'lucide-react'
import type { ApInvoiceProgress, ApInvoiceProgressStep } from '@/features/invoices/types'
import { cn, parseUTCDate } from '@/lib/utils'

const STATE_STYLE: Record<
  ApInvoiceProgressStep['state'],
  { icon: typeof Circle; icon_class: string; status_class: string; line_class: string }
> = {
  done: {
    icon: CheckCircle2,
    icon_class: 'text-emerald-600',
    status_class: 'text-emerald-700',
    line_class: 'bg-emerald-600/40',
  },
  skipped: {
    icon: MinusCircle,
    icon_class: 'text-muted-foreground',
    status_class: 'text-muted-foreground',
    line_class: 'bg-border',
  },
  current: {
    icon: Clock3,
    icon_class: 'text-amber-600',
    status_class: 'text-amber-700',
    line_class: 'bg-border',
  },
  upcoming: {
    icon: Circle,
    icon_class: 'text-muted-foreground/60',
    status_class: 'text-muted-foreground',
    line_class: 'bg-border',
  },
}

function shortDay(at: string) {
  const date = parseUTCDate(at)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

/** Where the invoice is in approval and payment: who did each step and when, or why it was skipped (USA-1632). */
export function InvoiceProgress({ progress }: { progress: ApInvoiceProgress | undefined }) {
  if (!progress) return null
  const steps = progress.steps
  return (
    <ol
      aria-label="Approval and payment progress"
      className="grid gap-3 rounded-lg border bg-card px-4 py-3 xl:grid-cols-5 xl:gap-4"
    >
      {steps.map((step, index) => {
        const style = STATE_STYLE[step.state]
        const Icon = style.icon
        const last = index === steps.length - 1
        return (
          <li
            key={step.key}
            aria-current={step.state === 'current' ? 'step' : undefined}
            className="grid min-w-0 grid-cols-[1.25rem_1fr] content-start gap-x-2"
          >
            <Icon className={cn('size-5', style.icon_class)} aria-hidden="true" />
            <div className="flex h-5 min-w-0 items-center gap-2">
              <span className="text-sm font-medium">{step.label}</span>
              {!last && (
                <span aria-hidden="true" className={cn('hidden h-px flex-1 xl:block', style.line_class)} />
              )}
            </div>
            <p className={cn('col-start-2 text-xs font-medium', style.status_class)}>
              <span className="sr-only">{step.state}: </span>
              {step.status}
              {step.at && <span className="whitespace-nowrap font-normal text-muted-foreground"> · {shortDay(step.at)}</span>}
            </p>
            {step.reason && (
              <p className="col-start-2 line-clamp-2 text-xs text-muted-foreground" title={step.reason}>
                {step.reason}
              </p>
            )}
          </li>
        )
      })}
    </ol>
  )
}
